import BestOfferPriceDetailsOptionsDetails from '@ui/Accommodation/BestOffer/PriceDetails/OptionsDetails/component'
import CheckoutAccess from '@ui/Accommodation/CheckoutAccess/component'
import AccommodationDetailsConditions from '@ui/Accommodation/Details/Conditions/component'
import BookingReward from '@ui/Accommodation/Offer/BookingReward/component'
import { hideLoading } from '@ui/Button/component'
import Flags from '@ui/Flags/component'
import ImagesSlider from '@ui/ImagesSlider/component'
import MobileBottomPanel from '@ui/MobileBottomPanel/component'
import SidePanel from '@ui/SidePanel/component'
import Slider from '@ui/Slider/component'
import tooltipV2 from '@ui/Tooltip/component'
import ZoomMediasPanel from '@ui/ZoomMediasPanel/component'
import * as coreCommon from 'assets/core/js/common'
import ElementPropertiesManager from 'assets/core/js/module/elementPropertiesManager'
import almaWidgets from 'assets/core/js/module/payment/alma/widgets'
import paypalPayLater from 'assets/core/js/module/payment/paypal/payLater'
import stickyObserver from 'assets/core/js/module/stickyObserver'
import type { SidePanelConfig } from '@design/objects/sidePanel/twig/assets/index'
import type { SidePanelType } from '@ui/SidePanel/component'

export default (id: string, url: string, type: 'accommodation' | 'pitch', accommodationId: string): SidePanelType | undefined => {
  const sidePanelConfig: SidePanelConfig = {
    rootEl: document.body,
    onOpen: function () {
      document.body.dispatchEvent(new CustomEvent('sidePanelAccommodation.modal.opened', { detail: { accommodationId } }))
    },
    onClose: function (sidePanel: SidePanelType) {
      document.body.dispatchEvent(new CustomEvent('sidePanelAccommodation.modal.closed'))

      const checkOfferForm = sidePanel.getElement().querySelector<HTMLFormElement>('form[data-check-offer-id]')

      if (checkOfferForm) {
        const submitEl = checkOfferForm.querySelector<HTMLButtonElement>('[type="submit"]')

        if (!submitEl) {
          return
        }

        if (!submitEl.classList.contains('o-button--with-loading')) {
          return
        }

        hideLoading(submitEl)
      }
    },
    onContentSet: function (sidePanel) {
      const sidePanelEl = sidePanel.getElement()
      const images = sidePanelEl.querySelectorAll<HTMLImageElement>('.product__images-mosaic img')
      const totalProductImages = images.length

      initOptionsSlider(sidePanelEl)
      initScrollObserver(sidePanelEl)

      if (type === 'pitch') {
        initPitchMobileBottomPanel(sidePanelEl, accommodationId)
        stickyObserver()
      } else if (type === 'accommodation') {
        initScrollToAvailability(sidePanelEl)
        initPaypalPayLater(sidePanelEl)
        initAlmaWidgets(sidePanelEl)
      }

      if (totalProductImages && totalProductImages > 0) {
        ImagesSlider(sidePanelEl.querySelector('.product__images-mosaic') as HTMLElement, {
          loop: false,
          pagination: {
            el: '.slider-pagination',
            type: 'custom',
            renderCustom: function (current: number, total: number) {
              return `${current}/${total}`
            },
          },
          spaceBetween: 10,
        })

        images.forEach((img) => {
          img.addEventListener('click', () => {
            initZoomMediasPanel(img)
          })
        })
      }

      const checkOfferForm = sidePanelEl.querySelector<HTMLFormElement>('form[data-check-offer-id]')

      if (checkOfferForm) {
        CheckoutAccess(checkOfferForm)

        checkOfferForm.addEventListener('submit', function () {
          const event = new CustomEvent('checkOfferForm.submit', {
            detail: {
              accommodationId,
              flexible: {
                selected: false,
                amount: 0,
              },
            },
          })

          document.body.dispatchEvent(event)
        })
      }

      const accommodationFlags = sidePanelEl.querySelector<HTMLElement>('.accommodation-flags')
      if (accommodationFlags) {
        Flags(accommodationFlags)
      }

      initBestOfferTooltips(sidePanelEl)

      sidePanelEl.querySelectorAll<HTMLElement>('.accommodation-details-conditions').forEach((el) => {
        AccommodationDetailsConditions(el)
      })

      const offerRewardSimulation = sidePanelEl.querySelectorAll<HTMLElement>('.accommodation-offer-booking-reward')

      offerRewardSimulation.forEach((el) => {
        BookingReward(el)
      })

      sidePanelEl.querySelectorAll<HTMLElement>('.accommodation-card-best-offer-price-details-options-details').forEach((el) => {
        BestOfferPriceDetailsOptionsDetails(el)
      })
    },
  }

  const templateEl = document.getElementById(`${id}-template`) as HTMLElement

  if (!templateEl) {
    return
  }

  const sidePanelEl = templateEl.parentNode?.insertBefore<HTMLElement>(coreCommon.createNodeFromHTML(templateEl.innerHTML), templateEl)

  if (!sidePanelEl) {
    return
  }

  const sidePanel = SidePanel(sidePanelEl, sidePanelConfig)

  if (!sidePanel) {
    return
  }

  templateEl.remove()

  const xhrRequest = coreCommon.createXhr('GET', url)

  xhrRequest.onreadystatechange = () => {
    if (xhrRequest.readyState === XMLHttpRequest.DONE && xhrRequest.status === 200 && xhrRequest.response) {
      sidePanel.setContent(xhrRequest.response as string)
    } else if (xhrRequest.readyState === XMLHttpRequest.DONE && xhrRequest.status === 500) {
      sidePanel.close()
    }
  }

  xhrRequest.send()

  return sidePanel
}

const initBestOfferTooltips = (sidePanelEl: HTMLElement): void => {
  sidePanelEl.querySelectorAll<HTMLElement>('.offer-discount-info').forEach((containerEl) => {
    const triggerEl = containerEl.querySelector<HTMLElement>('.offer-discount-info-text')
    const tooltipEl = containerEl.querySelector<HTMLElement>('.offer-discount-info-tooltip')

    if (triggerEl && tooltipEl) {
      tooltipV2(triggerEl, tooltipEl)
    }
  })
}

const initScrollToAvailability = (sidePanelEl: HTMLElement): void => {
  const availabilityPriceEl = sidePanelEl.querySelector<HTMLElement>('.accommodation-sidepanel-bottom .availability__details-prices')

  if (!availabilityPriceEl) {
    return
  }

  availabilityPriceEl.addEventListener('click', () => {
    sidePanelEl.querySelector<HTMLElement>('.sidebar-content')?.scrollIntoView()
  })
}

const initOptionsSlider = (sidePanelEl: HTMLElement): void => {
  const optionsListEl = sidePanelEl.querySelector<HTMLElement>('.accommodation__options-list')
  if (!optionsListEl) {
    return
  }

  Slider(optionsListEl, {
    loop: false,
    slidesPerView: 'auto',
    slidesPerGroup: 3,
    navigation: {
      nextEl: optionsListEl.querySelector('.slider-button-next') as HTMLElement,
      prevEl: optionsListEl.querySelector('.slider-button-prev') as HTMLElement,
    },
    on: {
      resize: (slider) => {
        if (!coreCommon.isMobile()) {
          slider.update({
            slidesPerGroup: 6,
          })
        } else {
          slider.update({
            slidesPerGroup: 3,
          })
        }
      },
    },
  })
}

const initPaypalPayLater = (sidePanel: HTMLElement): void => {
  const paypalMessages = sidePanel.querySelectorAll<HTMLElement>('[data-cpp-message]')

  paypalMessages.forEach((paypalMessage) => {
    void paypalPayLater(paypalMessage)
  })
}

const initAlmaWidgets = (sidePanel: HTMLElement): void => {
  const widgets = sidePanel.querySelectorAll<HTMLElement>('[data-alma-widget]')

  widgets.forEach((widget) => {
    void almaWidgets(widget)
  })
}

const initScrollObserver = (sidePanel: HTMLElement): void => {
  const headingEl = sidePanel.querySelector('.o-side-panel__heading')
  const observedEl = sidePanel.querySelector('.accommodation-name')
  const scrollableEl = sidePanel.querySelector<HTMLElement>('.accommodation-sidepanel-content-wrapper')

  if (!observedEl || !headingEl || !scrollableEl) {
    return
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) {
          headingEl.setAttribute('data-sticky', 'true')
        } else {
          headingEl.removeAttribute('data-sticky')
        }
      })
    },
    {
      root: scrollableEl,
    }
  )

  observer.observe(observedEl)
}

const initZoomMediasPanel = (element: HTMLElement): void => {
  let elementWithAttributes: HTMLElement | null = element

  if (!element.hasAttribute('data-zoom-id')) {
    elementWithAttributes = element.closest<HTMLElement>('[data-zoom-id]')
  }

  if (!elementWithAttributes) {
    return
  }

  const id = elementWithAttributes.getAttribute('data-zoom-id')

  if (!id) {
    return
  }

  const zoomMediasPanelEl = document.getElementById(id)
  let zoomMediasPanel: ZoomMediasPanel | undefined = zoomMediasPanelEl
    ? ElementPropertiesManager.getProperty(zoomMediasPanelEl, 'zoomMediasPanel')
    : undefined

  if (!zoomMediasPanel) {
    const templateEl = document.getElementById(`${id}-template`) as HTMLElement

    if (!templateEl) {
      return
    }

    templateEl.parentNode?.insertBefore<HTMLElement>(coreCommon.createNodeFromHTML(templateEl.innerHTML), templateEl)
    templateEl.remove()

    zoomMediasPanel = new ZoomMediasPanel(id, {
      onInit: (zoom: ZoomMediasPanel) => {
        zoom.open()
      },
      onContentSet: function (zoom: ZoomMediasPanel) {
        if (element.hasAttribute('data-index')) {
          zoom?.goToMedia(element.getAttribute('data-index') as string)
        }
      },
    })
  } else {
    if (element.hasAttribute('data-index')) {
      zoomMediasPanel?.goToMedia(element.getAttribute('data-index') as string)
    } else {
      zoomMediasPanel?.open()
    }
  }
}

const initPitchMobileBottomPanel = (element: HTMLElement, accommodationId: string): void => {
  const el = document.getElementById(`accommodation-mobile-bottompanel-${accommodationId}`)

  if (!el) {
    return
  }

  const panel = MobileBottomPanel(el)

  element.querySelectorAll(`[data-panel-target="accommodation-mobile-bottompanel-${accommodationId}"]`).forEach((targetEl) => {
    targetEl.addEventListener('click', () => {
      panel.toggle()
    })
  })
}
